import { useEffect, useState } from 'react';
import { api } from '../../../services/api/apiClient';
import { labelOf } from '../../../utils/labels';
import { JOB_STATUS } from '../../../constants/status';
import { money } from '../../../utils/currency';
import { MultiSelect } from '../../ui/Select/Select';
import { SearchSelect, SearchSelectProps } from '../SearchSelect/SearchSelect';
import type { MultiSelectProps } from '../../ui/Select/Select';

type PickerProps = Omit<SearchSelectProps, 'fetcher'> & { query?: Record<string, any> };

const list = (path: string, query?: Record<string, any>) => (search: string): Promise<any[]> => api.get(path, { search, limit: 20, ...query }).then((r) => r.data);

export function CustomerPicker(props: PickerProps) {
  return (
    <SearchSelect
      label="Customer"
      placeholder="Search customer by name or phone"
      fetcher={list('/customers', { isActive: 'true', sort: 'name' })}
      getDescription={(c) => [c.phone, c.billingAddress?.city].filter(Boolean).join(' · ') || c.customerCode}
      {...props}
    />
  );
}

export function VendorPicker(props: PickerProps) {
  return (
    <SearchSelect
      label="Supplier"
      placeholder="Search supplier"
      fetcher={list('/vendors', { isActive: 'true', sort: 'name' })}
      getDescription={(v) => [v.phone, v.vendorCode].filter(Boolean).join(' · ')}
      {...props}
    />
  );
}

/** type: 'service' (labour) | 'material' | undefined for both. */
export function ItemPicker({ type, ...props }: PickerProps & { type?: 'service' | 'material' }) {
  return (
    <SearchSelect
      placeholder={type === 'service' ? 'Pick from rate card' : type === 'material' ? 'Pick material' : 'Pick material or work'}
      fetcher={list('/items', { type, isActive: 'true', sort: 'name' })}
      getLabel={(i) => i.name}
      getDescription={(i) =>
        (i.type === 'material'
          ? [i.brand, i.specification, `${money(i.sellingRate)}/${i.unit}`, `${i.currentStock ?? 0} in stock`]
          : [i.category, `${money(i.sellingRate)} per ${i.unit}`])
          .filter(Boolean)
          .join(' · ')
      }
      {...props}
    />
  );
}

export function JobPicker({ query, ...props }: PickerProps) {
  return (
    <SearchSelect
      label="Service job"
      placeholder="Search job no, customer"
      fetcher={list('/services', query)}
      getLabel={(s) => `${s.serviceNo} · ${s.customer?.name ?? ''}`}
      getDescription={(s) => [s.title, labelOf(JOB_STATUS, s.status)].filter(Boolean).join(' · ')}
      {...props}
    />
  );
}

export function InvoicePicker({ query, ...props }: PickerProps) {
  return (
    <SearchSelect
      label="Invoice"
      placeholder="Search invoice no or customer"
      fetcher={list('/invoices', { status: 'final', ...query })}
      getLabel={(i) => `${i.invoiceNo} · ${i.customer?.name ?? ''}`}
      getDescription={(i) => `Balance ${money(i.balanceAmount)} of ${money(i.grandTotal)}`}
      {...props}
    />
  );
}

export function PurchasePicker({ query, ...props }: PickerProps) {
  return (
    <SearchSelect
      label="Supplier bill"
      placeholder="Search purchase no or supplier"
      fetcher={list('/purchases', { status: 'active', ...query })}
      getLabel={(p) => `${p.purchaseNo} · ${p.vendor?.name ?? ''}`}
      getDescription={(p) => `Balance ${money(p.balanceAmount)} of ${money(p.grandTotal)}${p.vendorBillNo ? ` · Bill ${p.vendorBillNo}` : ''}`}
      {...props}
    />
  );
}

export function StaffPicker({ role, ...props }: PickerProps & { role?: string }) {
  return (
    <SearchSelect
      label="Staff member"
      placeholder="Search staff"
      fetcher={list('/users', { role, isActive: 'true', sort: 'name' })}
      getDescription={(u) => [u.phone, u.role].filter(Boolean).join(' · ')}
      {...props}
    />
  );
}

/** Pick one or more workers for a job. */
export function WorkerMultiSelect(props: MultiSelectProps<string>) {
  const [options, setOptions] = useState<{ value: string; label: string; description?: string }[]>([]);
  useEffect(() => {
    api
      .get('/users', { role: 'worker', isActive: 'true', limit: 200 })
      .then((r) => setOptions(r.data.map((u: any) => ({ value: u._id, label: u.name, description: u.phone }))))
      .catch(() => setOptions([]));
  }, []);
  return <MultiSelect label="Workers" placeholder="Pick workers" options={options} emptyText="No active workers. Add them in Team." {...props} />;
}
